import * as momentTimezone from 'moment-timezone';

import { LocalStorageKeys, LocalStorageService } from 'src/services/localStorage';

export interface TimezoneOption {
  key: string;
  val: string;
}

export interface TimezoneGroup {
  group: string;
  options: TimezoneOption[];
}

/**
 * The single label form for a timezone — `UTC +4:00 — Asia/Dubai`.
 *
 * Shared by every timezone picker option and by the dispatch chip that opens one, so the
 * chip reads back exactly what the operator picked from the list.
 */
export const formatOffsetLabel = (tzName: string): string => {
  const offset = momentTimezone.tz(tzName).utcOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset / 60);
  const minutes = absOffset % 60;
  const padded = `${hours}:${minutes.toString().padStart(2, '0')}`;
  return `UTC ${sign}${padded} — ${tzName}`;
};

const GROUP_ORDER = [
  'Africa', 'America', 'Antarctica', 'Arctic', 'Asia',
  'Atlantic', 'Australia', 'Europe', 'Indian', 'Pacific',
];

const buildTimezoneList = (): TimezoneGroup[] => {
  const allNames = momentTimezone.tz.names();
  const grouped: Record<string, TimezoneOption[]> = {};
  const other: TimezoneOption[] = [];

  allNames.forEach((name) => {
    const slashIndex = name.indexOf('/');

    if (slashIndex === -1) {
      other.push({ key: name, val: formatOffsetLabel(name) });
      return;
    }

    const region = name.substring(0, slashIndex);
    if (!GROUP_ORDER.includes(region)) {
      other.push({ key: name, val: formatOffsetLabel(name) });
      return;
    }

    if (!grouped[region]) {
      grouped[region] = [];
    }
    grouped[region].push({ key: name, val: formatOffsetLabel(name) });
  });

  Object.keys(grouped).forEach((region) => {
    grouped[region].sort((a, b) => {
      const offsetA = momentTimezone.tz(a.key).utcOffset();
      const offsetB = momentTimezone.tz(b.key).utcOffset();
      if (offsetA !== offsetB) return offsetA - offsetB;
      return a.key.localeCompare(b.key);
    });
  });

  other.sort((a, b) => {
    const offsetA = momentTimezone.tz(a.key).utcOffset();
    const offsetB = momentTimezone.tz(b.key).utcOffset();
    if (offsetA !== offsetB) return offsetA - offsetB;
    return a.key.localeCompare(b.key);
  });

  const result: TimezoneGroup[] = [];

  GROUP_ORDER
    .filter((region) => grouped[region] && grouped[region].length > 0)
    .forEach((region) => result.push({ group: region, options: grouped[region] }));

  if (other.length > 0) {
    result.push({ group: 'Other', options: other });
  }

  return result;
};

export const TIMEZONE_LIST: TimezoneGroup[] = buildTimezoneList();

export const getActiveTimezone = (): string => {
  return LocalStorageService.getRaw(LocalStorageKeys.SHIPSY_TIMEZONE)
    || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export interface TimezoneDisplay {
  utcOffset: string;
  gmtOffset: string;
  tz: string;
  fullDisplay: string;
  dateFooterLabel: string;
}

export const getTimezoneDisplay = (): TimezoneDisplay => {
  const tz = getActiveTimezone();
  const offset = momentTimezone.tz(tz).utcOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset / 60);
  const minutes = absOffset % 60;
  const padded = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const utcOffset = `UTC ${sign}${padded}`;
  const gmtOffset = `GMT ${sign}${padded}`;
  const fullDisplay = `${utcOffset} (${gmtOffset}, ${tz})`;
  const dateFooterLabel = `Dates shown in ${tz} (UTC${sign}${padded})`;
  return { utcOffset, gmtOffset, tz, fullDisplay, dateFooterLabel };
};

export function formatDateTimeTz(dateTime: any): string {
  if (!dateTime) return 'Not Available';
  return momentTimezone(dateTime).tz(getActiveTimezone()).format('DD MMM YYYY, hh:mm A');
}

export function formatDateTz(date: any, nullOption: string = '---'): string {
  if (!date) return nullOption;
  return momentTimezone(date).tz(getActiveTimezone()).format('DD MMM, YYYY');
}

export function formatTimeTz(time: any): string {
  if (!time) return '---';
  return momentTimezone(time).tz(getActiveTimezone()).format('hh:mm A');
}

export function formatTimeSlotTz(dateTime1: any, dateTime2: any): string | undefined {
  const tz = getActiveTimezone();
  const hr1 = dateTime1 ? momentTimezone(dateTime1).tz(tz).format('hh A') : undefined;
  const hr2 = dateTime2 ? momentTimezone(dateTime2).tz(tz).format('hh A') : undefined;
  const time = hr1 && hr2 && (hr1 + ' - ' + hr2);
  const date = momentTimezone(dateTime1).tz(tz).format('DD MMM YYYY');
  return time ? time + '    ' + date : undefined;
}

const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Returns the effective timezone for the current hub.
 * Fallback: hub.timezone → masterData.organisation_timezone → DEFAULT_TIMEZONE
 */
export const getHubTimezone = (currHub: any, orgTimezone?: string): string => {
  return currHub?.timezone || orgTimezone || DEFAULT_TIMEZONE;
};

/**
 * Returns current moment in hub timezone. Replacement for moment().
 */
export const hubCurrentMoment = (currHub: any, orgTimezone?: string): momentTimezone.Moment => {
  return momentTimezone.tz(getHubTimezone(currHub, orgTimezone));
};

/**
 * Returns today's date string (YYYY-MM-DD) in hub timezone.
 */
export const hubTodayDate = (currHub: any, orgTimezone?: string): string => {
  return hubCurrentMoment(currHub, orgTimezone).format('YYYY-MM-DD');
};

/**
 * Parses a date and time string into a moment object in the hub's timezone.
 */
export const hubParse = (
  dateStr: string | undefined, timeStr: string, currHub: any, orgTimezone?: string,
): momentTimezone.Moment => {
  if (!dateStr) return momentTimezone.invalid();
  const tz = getHubTimezone(currHub, orgTimezone);
  return momentTimezone.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', tz);
};

/**
 * Returns display info for the timezone badge component.
 */
export interface HubTimezoneInfo {
  label: string;
  offset: string;
  ianaName: string;
  isFallback: boolean;
}

export const getHubTimezoneDisplayInfo = (currHub: any, orgTimezone?: string): HubTimezoneInfo => {
  const isFallback = !currHub?.timezone;
  const tz = getHubTimezone(currHub, orgTimezone);
  const m = momentTimezone.tz(tz);
  return {
    label: m.zoneAbbr(),
    offset: `GMT${m.format('Z')}`,
    ianaName: tz,
    isFallback,
  };
};
